import apiClient from "./apiClient";
import { API_PAGINATION_LIMITS } from "../constants/api-limits";

// ============================================================
// Barometer API (BC: Catalog)
// @see docs/specifications/2026-03-06-kfs-barometr-zawodow.md
// ============================================================

import { BarometerResponse } from "../types/grants";
import type { ArticleAttachment } from "../components/grants/shared/article-attachment.types";

// Grants Articles API
export const getGrantsArticles = async (params?: {
  page?: number;
  limit?: number;
  province?: string;
  district?: string;
}) => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append("page", params.page.toString());
  if (params?.limit) searchParams.append("limit", params.limit.toString());
  if (params?.province) searchParams.append("province", params.province);
  if (params?.district) searchParams.append("district", params.district);

  const { data } = await apiClient.get(
    `/grants/kfs/articles/list?${searchParams.toString()}`,
  );
  return data;
};

export const getGrantsArticle = async (id: string) => {
  const { data } = await apiClient.get(`/grants/kfs/articles/${id}`);
  return data;
};

// Grants KFS API
export const getGrantsKfs = async (params?: {
  page?: number;
  limit?: number;
  province?: string;
  district?: string;
}) => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append("page", params.page.toString());
  if (params?.limit) searchParams.append("limit", params.limit.toString());
  if (params?.province) searchParams.append("province", params.province);
  if (params?.district) searchParams.append("district", params.district);

  const { data } = await apiClient.get(
    `/grants/kfs/list?${searchParams.toString()}`,
  );
  return data;
};

export const getGrantsKf = async (id: string) => {
  const { data } = await apiClient.get(`/grants/kfs/articles/${id}`);
  return data;
};

// Grants Indexer Management
export const triggerGrantsIndexing = async () => {
  const { data } = await apiClient.post("/grants/kfs/trigger");
  return data;
};

export const getGrantsIndexingStatus = async () => {
  const { data } = await apiClient.get("/grants/kfs/status");
  return data;
};

// General Grants API (if needed for other grant operations)
export const getGrants = async () => {
  const { data } = await apiClient.get("/grants");
  return data;
};

export const getGrant = async (id: string) => {
  const { data } = await apiClient.get(`/grants/${id}`);
  return data;
};

// Grants Notifications API
export interface NotificationSettings {
  id?: string;
  isEnabled: boolean;
  notificationMode: "IMMEDIATE" | "SCHEDULED";
  scheduleHours: number[];
  timezone: string;
  sources?: string[];
  // Filtry geograficzne
  kfsVoivodeships?: string[];
  kfsCounties?: string[];
  burVoivodeships?: string[];
  lastNotificationSent?: Date;
}

export interface NotificationSettingsResponse {
  id: string;
  isEnabled: boolean;
  notificationMode: "IMMEDIATE" | "SCHEDULED";
  scheduleHours: number[];
  timezone: string;
  sources: string[];
  // Filtry geograficzne
  kfsVoivodeships: string[];
  kfsCounties: string[];
  burVoivodeships: string[];
  lastNotificationSent?: string;
  message?: string;
}

export const getNotificationSettings =
  async (): Promise<NotificationSettingsResponse> => {
    const { data } = await apiClient.get("/grants/notifications/settings");
    return data;
  };

export const updateNotificationSettings = async (settings: {
  isEnabled?: boolean;
  notificationMode?: "IMMEDIATE" | "SCHEDULED";
  scheduleHours?: number[];
  timezone?: string;
  sources?: string[];
  // Filtry geograficzne
  kfsVoivodeships?: string[];
  kfsCounties?: string[];
  burVoivodeships?: string[];
}): Promise<NotificationSettingsResponse> => {
  const { data } = await apiClient.put(
    "/grants/notifications/settings",
    settings,
  );
  return data;
};

export const sendTestNotification = async (): Promise<{
  message: string;
  articlesCount: number;
}> => {
  const { data } = await apiClient.post("/grants/notifications/test");
  return data;
};

// BUR Grants API
export const getBurArticles = async (params?: {
  page?: number;
  limit?: number;
  voivodeship?: string;
  sortBy?: string;
  sortOrder?: string;
}) => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append("page", params.page.toString());
  if (params?.limit) searchParams.append("limit", params.limit.toString());
  if (params?.voivodeship)
    searchParams.append("voivodeship", params.voivodeship);
  if (params?.sortBy) searchParams.append("sortBy", params.sortBy);
  if (params?.sortOrder) searchParams.append("sortOrder", params.sortOrder);

  const { data } = await apiClient.get(
    `/grants/bur/articles?${searchParams.toString()}`,
  );
  return data;
};

export const getBurGrants = async (params?: {
  page?: number;
  limit?: number;
  voivodeship?: string;
  sortBy?: string;
  sortOrder?: string;
}) => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append("page", params.page.toString());
  if (params?.limit) searchParams.append("limit", params.limit.toString());
  if (params?.voivodeship)
    searchParams.append("voivodeship", params.voivodeship);
  if (params?.sortBy) searchParams.append("sortBy", params.sortBy);
  if (params?.sortOrder) searchParams.append("sortOrder", params.sortOrder);

  const { data } = await apiClient.get(
    `/grants/bur/articles/grants?${searchParams.toString()}`,
  );
  return data;
};

export const getBurArticle = async (id: string) => {
  const { data } = await apiClient.get(`/grants/bur/articles/${id}`);
  return data;
};

// Article Reprocessing API
export interface ReprocessArticleOptions {
  skipNotifications?: boolean;
  forceReprocess?: boolean;
}

export const reprocessKfsArticle = async (
  articleId: string,
  options: ReprocessArticleOptions = {},
) => {
  const payload: Record<string, boolean> = {};
  if (options.skipNotifications) {
    payload.skipNotifications = true;
  }
  if (options.forceReprocess) {
    payload.forceReprocess = true;
  }
  return apiClient.post(`/kfs/process-article/${articleId}`, payload);
};

export const closeKfsArticle = async (articleId: string) => {
  return apiClient.post(`/kfs/articles/${articleId}/close`);
};

export const closeBurArticle = async (articleId: number) => {
  return apiClient.post(`/grants/bur/articles/${articleId}/close`);
};

export const reprocessBurArticle = async (
  articleId: number,
  options: ReprocessArticleOptions = {},
) => {
  const payload: Record<string, boolean> = {};
  if (options.skipNotifications) {
    payload.skipNotifications = true;
  }
  if (options.forceReprocess) {
    payload.forceReprocess = true;
  }
  return apiClient.post(`/grants/bur/articles/${articleId}/process`, payload);
};

// Article Reporting API
export const reportKfsArticle = async (
  articleId: string,
  reason?: string,
): Promise<{ message: string; articleId: string }> => {
  const { data } = await apiClient.post(
    `/kfs/articles/${articleId}/report`,
    reason ? { reason } : {},
  );
  return data;
};

export const reportBurArticle = async (
  articleId: number,
  reason?: string,
): Promise<{ message: string; articleId: number }> => {
  const { data } = await apiClient.post(
    `/grants/bur/articles/${articleId}/report`,
    reason ? { reason } : {},
  );
  return data;
};

// Article Flags Update API
export interface UpdateKfsArticleFlagsPayload {
  applicationStartDate?: string | null;
  applicationEndDate?: string | null;
  hasKfsFlag?: boolean;
  isReserve?: boolean;
}

export interface UpdateKfsArticleFlagsResponse {
  message: string;
  articleId: string;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  hasKfsFlag: boolean;
  isReserve: boolean;
}

export const updateKfsArticleFlags = async (
  articleId: string,
  payload: UpdateKfsArticleFlagsPayload,
): Promise<UpdateKfsArticleFlagsResponse> => {
  const { data } = await apiClient.patch(
    `/kfs/articles/${articleId}/flags`,
    payload,
  );
  return data;
};

export interface UpdateBurArticleFlagsPayload {
  applicationDateFrom?: string | null;
  applicationDateTo?: string | null;
  isBur?: boolean;
  isContinuous?: boolean;
  isAloc?: boolean;
  targetOverride?: string | null;
  operatorId?: number;
}

export interface UpdateBurArticleFlagsResponse {
  message: string;
  articleId: number;
  applicationDateFrom: string | null;
  applicationDateTo: string | null;
  isBur: boolean;
  isContinuous: boolean;
  isAloc: boolean;
  targetOverride: string | null;
}

export const updateBurArticleFlags = async (
  articleId: number,
  payload: UpdateBurArticleFlagsPayload,
): Promise<UpdateBurArticleFlagsResponse> => {
  const { data } = await apiClient.patch(
    `/grants/bur/articles/${articleId}/flags`,
    payload,
  );
  return data;
};

// ============================================================
// Admin Recommendations API (BC: Calls)
// ============================================================

/**
 * Operator in context chain
 * @see docs/specifications/2026-01-29-recommendation-modal-context-links.md
 */
export interface RecommendationContextOperator {
  id: string;
  name: string;
  website: string | null;
}

/**
 * Competition in context chain
 * @see docs/specifications/2026-01-29-recommendation-modal-context-links.md
 */
export interface RecommendationContextCompetition {
  id: string;
  name: string;
}

/**
 * Project in context chain
 * @see docs/specifications/2026-01-29-recommendation-modal-context-links.md
 */
export interface RecommendationContextProject {
  id: string;
  name: string;
  projectUrl: string | null;
  operator: RecommendationContextOperator | null;
  competition: RecommendationContextCompetition | null;
}

/**
 * Information source in context chain
 * @see docs/specifications/2026-01-29-recommendation-modal-context-links.md
 */
export interface RecommendationContextSource {
  id: string;
  name: string;
  url: string | null;
  sourceType: string;
  project: RecommendationContextProject | null;
}

/**
 * Source-article link representing M:N relationship between articles and sources.
 *
 * This interface models the join table that connects articles to information sources.
 * The full context chain is:
 *   Recommendation -> Article -> SourceLinks[] -> Source -> Project -> Operator
 *
 * An article can be discovered from multiple sources (M:N), but for display purposes
 * we typically use the first source link to show the context hierarchy.
 *
 * @example
 * // Traversing the context chain:
 * const firstLink = recommendation.article.sourceLinks[0];
 * const source = firstLink.source;           // InformationSource
 * const project = source.project;            // GrantsProject
 * const operator = project?.operator;        // GrantsOperator
 *
 * @see RecommendationContextSource - The source entity in the chain
 * @see RecommendationContextProject - The project entity in the chain
 * @see RecommendationContextOperator - The operator entity in the chain
 * @see docs/specifications/2026-01-29-recommendation-modal-context-links.md
 */
export interface RecommendationSourceLink {
  /** The information source this article was discovered from */
  source: RecommendationContextSource;
}

/**
 * Article with deep context relations for details page
 * @see docs/specifications/2026-01-29-recommendation-modal-context-links.md
 */
export interface RecommendationArticle {
  id: string;
  title: string;
  url: string;
  rawContent?: string | null;
  createdAt?: string;
  sourceLinks?: RecommendationSourceLink[];
  /** Attachments (PDF/DOCX/IMAGE/DOC/XLS/XLSX) extracted from the article, shape = PublicArticleAttachment */
  attachments?: ArticleAttachment[];
}

export interface RecommendationTargetCall {
  id: string;
  title: string;
  status?: string;
  // Extended fields for diff comparison (UI improvements)
  applicationStartDate?: string | null;
  applicationEndDate?: string | null;
  allocation?: number | null;
  targetRegions?: string[];
  targetGroups?: string[];
  maxParticipants?: number | null;
  callNumber?: string | null;
}

/**
 * Whether the recommendation's article is already linked to the target call.
 * Present only for UPDATE/LINK recommendations (null for CREATE_NEW).
 */
export interface RecommendationTargetCallArticleLink {
  linked: boolean;
  /** ISO timestamp when the article was added to the call (null if not linked) */
  addedAt: string | null;
  /** ArticleType value of the existing mapping (null if not linked) */
  articleRole: string | null;
  /** How the existing mapping was created (AUTO_LLM / AUTO_URL / MANUAL) */
  mappingType: string | null;
  /** True when matched a different article record sharing the same URL */
  matchedByUrl: boolean;
}

export interface CallRecommendation {
  id: string;
  articleId: string;
  analysisId: string | null;
  suggestedAction: "create_new" | "update_existing" | "link_to_existing";
  targetCallId: string | null;
  confidence: number;
  reason: string;
  extractedDataSnapshot?: Record<string, unknown>;
  /** Merged snapshot after moderator edits (if any overrides were applied) */
  approvedDataSnapshot?: Record<string, unknown> | null;
  status: "pending_review" | "approved" | "rejected" | "auto_approved";
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  resultCallId?: string | null;
  createdAt: string;
  article: RecommendationArticle | null;
  targetCall: RecommendationTargetCall | null;
  resultCall?: RecommendationTargetCall | null;
  /** Count of related recommendations in same project (preloaded for collapsed header) */
  relatedRecommendationsCount?: number;
  /** Whether the article is already on the target call (UPDATE/LINK only; null for CREATE_NEW) */
  targetCallArticleLink?: RecommendationTargetCallArticleLink | null;
}

export interface RecommendationsResponse {
  data: CallRecommendation[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export const getAdminRecommendations = async (params?: {
  page?: number;
  limit?: number;
  status?: "all" | "pending_review" | "approved" | "rejected" | "auto_approved";
  articleId?: string;
}): Promise<RecommendationsResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append("page", params.page.toString());
  if (params?.limit) searchParams.append("limit", params.limit.toString());
  if (params?.status) searchParams.append("status", params.status);
  if (params?.articleId) searchParams.append("articleId", params.articleId);

  const { data } = await apiClient.get(
    `/admin/grants/recommendations?${searchParams.toString()}`,
  );
  return data;
};

export const getAdminRecommendationById = async (
  id: string,
): Promise<CallRecommendation> => {
  const { data } = await apiClient.get(`/admin/grants/recommendations/${id}`);
  return data;
};

export interface ApproveRecommendationResponse {
  success: boolean;
  callId: string;
  action: string;
  isNewCall: boolean;
}

/**
 * Overrides for editable recommendation fields.
 * Only changed fields should be included.
 * @see docs/specifications/2026-03-09-editable-recommendation-fields.md
 */
export interface RecommendationOverrides {
  title?: string;
  callNumber?: string;
  applicationStartDate?: string | null;
  applicationEndDate?: string | null;
  allocation?: number | null;
  maxParticipants?: number | null;
  regions?: string[];
  targetGroups?: string[];
  status?: string | null;
  /** Article character (ArticleType enum), overrides LLM-proposed value at approval. */
  articleCharacter?: string | null;
}

export const approveRecommendation = async (
  id: string,
  notes?: string,
  overrides?: RecommendationOverrides,
  // AC-5: moderator override of the suggested action, before approval.
  // Server allows only the update_existing -> create_new transition (every
  // other value is rejected) and requires overrides.callNumber to be set
  // (see ApproveRecommendationDto in recommendations.controller.ts).
  forcedAction?: "create_new",
): Promise<ApproveRecommendationResponse> => {
  const body: Record<string, unknown> = {};
  if (notes) body.notes = notes;
  if (overrides && Object.keys(overrides).length > 0)
    body.overrides = overrides;
  if (forcedAction) body.forcedAction = forcedAction;
  const { data } = await apiClient.post(
    `/admin/grants/recommendations/${id}/approve`,
    body,
  );
  return data;
};

export const rejectRecommendation = async (
  id: string,
  notes: string,
): Promise<{ success: boolean }> => {
  const { data } = await apiClient.post(
    `/admin/grants/recommendations/${id}/reject`,
    { notes },
  );
  return data;
};

export const revokeRecommendation = async (
  id: string,
  notes: string,
): Promise<{ success: boolean }> => {
  const { data } = await apiClient.post(
    `/admin/grants/recommendations/${id}/revoke`,
    { notes },
  );
  return data;
};

// Bulk operations types
export interface BulkOperationResult {
  success: number;
  failed: number;
  errors: Array<{ id: string; error: string }>;
  /**
   * AC-4: per-item approve outcome, present only for bulk-approve results.
   * Mirrors the single-recommendation approve response so a silent
   * create_new -> update_existing redirect (callNumber collision) is
   * visible on bulk-approved rows too, not just single approvals.
   */
  approved?: Array<{
    id: string;
    callId: string;
    action: string;
    isNewCall: boolean;
  }>;
}

export const bulkApproveRecommendations = async (
  recommendationIds: string[],
  notes?: string,
): Promise<BulkOperationResult> => {
  const { data } = await apiClient.post(
    "/admin/grants/recommendations/bulk-approve",
    { recommendationIds, ...(notes ? { notes } : {}) },
  );
  return data;
};

export const bulkRejectRecommendations = async (
  recommendationIds: string[],
  notes: string,
): Promise<BulkOperationResult> => {
  const { data } = await apiClient.post(
    "/admin/grants/recommendations/bulk-reject",
    { recommendationIds, notes },
  );
  return data;
};

/**
 * Gets the next pending recommendation, excluding the specified ID.
 * Returns null if no more pending recommendations exist.
 */
export const getNextPendingRecommendation = async (
  excludeId: string,
): Promise<CallRecommendation | null> => {
  const response = await getAdminRecommendations({
    status: "pending_review",
    limit: 10, // Fetch a few in case some need to be filtered
    page: 1,
  });

  // Find first recommendation that isn't the excluded one
  const nextRecommendation = response.data.find((rec) => rec.id !== excludeId);
  return nextRecommendation || null;
};

// ============================================================
// Admin Calls API (BC: Calls)
// ============================================================

export interface AdminCallListItem {
  id: string;
  title: string;
  callNumber?: string;
  status: string;
  applicationStartDate?: string;
  applicationEndDate?: string;
  program?: {
    id: string;
    code: string;
    name: string;
    shortName?: string;
  } | null;
  operator?: {
    id: string;
    name: string;
    shortName?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCallsListResponse {
  calls: AdminCallListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  statusCounts: Record<string, number>;
  programCounts: Record<string, number>;
}

export const getAdminCallsList = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  programId?: string;
}): Promise<AdminCallsListResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append("page", params.page.toString());
  if (params?.limit) searchParams.append("limit", params.limit.toString());
  if (params?.status) searchParams.append("status", params.status);
  if (params?.programId) searchParams.append("programId", params.programId);
  // Include all calls (not just active) for admin view
  searchParams.append("isActive", "false");

  const { data } = await apiClient.get(
    `/grants/calls?${searchParams.toString()}`,
  );
  return data;
};

// ============================================================
// Admin Calls API (BC: Calls - Audit Log)
// ============================================================

export interface AuditLogEntry {
  id: string;
  action: string;
  userType: "admin" | "system";
  userId: string | null;
  changes: Record<string, { old: unknown; new: unknown }> | null;
  createdAt: string;
}

export interface AdminCallDetails {
  id: string;
  title: string;
  callNumber?: string;
  status: string;
  programId?: string;
  operatorId?: string;
  applicationStartDate?: string;
  applicationEndDate?: string;
  allocation?: number;
  targetRegions?: string[];
  program?: {
    id: string;
    code: string;
    name: string;
    shortName?: string;
  } | null;
  operator?: {
    id: string;
    name: string;
    shortName?: string;
    type?: string;
  } | null;
  articles?: Array<{
    id: string;
    title: string;
    url: string;
    articleRole?: string;
  }>;
  auditLog?: AuditLogEntry[];
}

export const getAdminCallDetails = async (
  id: string,
): Promise<AdminCallDetails> => {
  const { data } = await apiClient.get(`/grants/calls/${id}`);
  return data;
};

export const getAdminCallAuditLog = async (
  callId: string,
): Promise<AuditLogEntry[]> => {
  const { data } = await apiClient.get(`/admin/grants/calls/${callId}/audit`);
  return data;
};

// ============================================================
// Article Recommendations & Analysis API (BC: Sourcing + Calls)
// ============================================================

export interface ArticleRecommendation {
  id: string;
  suggestedAction: "create_new" | "update_existing" | "link_to_existing";
  confidence: number;
  status: "pending_review" | "approved" | "rejected" | "auto_approved";
  targetCall: { id: string; title: string } | null;
  createdAt: string;
}

export interface ArticleRecommendationsResponse {
  recommendations: ArticleRecommendation[];
}

export const getArticleRecommendations = async (
  articleId: string,
): Promise<ArticleRecommendationsResponse> => {
  const { data } = await apiClient.get(
    `/grants/discovery-articles/${articleId}/recommendations`,
  );
  return data;
};

export interface ExtractedCallData {
  title?: string;
  callNumber?: string;
  status?: string;
  applicationStartDate?: string;
  applicationEndDate?: string;
  allocation?: { amount: number; currency: string } | number;
  targetRegions?: string[];
  targetGroups?: string[];
  maxParticipants?: number;
}

export interface ArticleAnalysisResponse {
  id: string | null;
  status: "none" | "PENDING" | "COMPLETED" | "SKIPPED" | "ERROR";
  extractedData: {
    calls?: ExtractedCallData[];
    metadata?: Record<string, unknown>;
  } | null;
  confidence: number | null;
  processingTimeMs: number | null;
  processedAt: string | null;
  error: string | null;
}

export const getArticleAnalysis = async (
  articleId: string,
): Promise<ArticleAnalysisResponse> => {
  const { data } = await apiClient.get(
    `/grants/discovery-articles/${articleId}/analysis`,
  );
  return data;
};

// ============================================================
// Manual Source Workflow API (BC: Sourcing)
// @see docs/specifications/2026-01-20-manual-source-phase3-frontend.md
// ============================================================

/** Request to add manual article to a MANUAL source */
export interface AddManualArticleRequest {
  title: string;
  url: string;
  content?: string;
  publishedAt?: string;
  fetchContent?: boolean;
}

/** Response after adding manual article */
export interface ManualArticleResponse {
  id: string;
  title: string;
  url: string;
  status: "DISCOVERED" | "CONTENT_FETCHED";
  message: string;
  isNewArticle: boolean;
}

/** Request to mark source as checked */
export interface MarkSourceCheckedRequest {
  note?: string;
}

/** Response after marking source checked */
export interface MarkSourceCheckedResponse {
  sourceId: string;
  checkedAt: string;
  checkedByUserId: string;
  nextCheckAt: string;
}

/** Recent article from a source */
export interface SourceRecentArticle {
  id: string;
  title: string;
  url: string;
  createdAt: string;
  addedByUserName: string | null;
}

/** Check URL uniqueness response */
export interface UrlCheckResponse {
  duplicate: boolean;
  articleId?: string;
  createdAt?: string;
}

/** API Error structure */
export interface ManualSourceApiError {
  code: string;
  message: string;
  statusCode: number;
  existingArticleId?: string;
}

/**
 * Add a manual article to a MANUAL source.
 * @throws 400 INVALID_URL - URL contains blocked addresses (SSRF)
 * @throws 400 SOURCE_NOT_MANUAL - Source is not MANUAL type
 * @throws 404 SOURCE_NOT_FOUND - Source does not exist
 * @throws 409 ARTICLE_ALREADY_LINKED - Article already linked to source
 */
export const addManualArticle = async (
  sourceId: string,
  data: AddManualArticleRequest,
): Promise<ManualArticleResponse> => {
  const response = await apiClient.post(
    `/grants/sources/${sourceId}/articles`,
    data,
  );
  return response.data;
};

/**
 * Mark a MANUAL source as checked.
 * Updates lastCheckedAt and schedules next check.
 * @throws 400 SOURCE_NOT_MANUAL - Source is not MANUAL type
 * @throws 404 SOURCE_NOT_FOUND - Source does not exist
 */
export const markSourceChecked = async (
  sourceId: string,
  data: MarkSourceCheckedRequest,
): Promise<MarkSourceCheckedResponse> => {
  const response = await apiClient.post(
    `/grants/sources/${sourceId}/mark-checked`,
    data,
  );
  return response.data;
};

/**
 * Get recent articles from a source.
 * Used to show recent articles in the add article form.
 */
export const getSourceRecentArticles = async (
  sourceId: string,
  limit = 5,
): Promise<SourceRecentArticle[]> => {
  const response = await apiClient.get(`/grants/sources/${sourceId}/articles`, {
    params: { limit },
  });
  return response.data;
};

/**
 * Check if a URL already exists in a source.
 * Used for soft validation before form submission.
 */
export const checkArticleUrlExists = async (
  sourceId: string,
  url: string,
): Promise<UrlCheckResponse> => {
  const response = await apiClient.get(
    `/grants/sources/${sourceId}/articles/check-url`,
    {
      params: { url },
    },
  );
  return response.data;
};

// ============================================================
// Project Context API (for Recommendation Details Page)
// @see docs/specifications/2026-01-30-recommendation-details-project-context.md
// ============================================================

/**
 * Call item for project calls section
 */
export interface ProjectCall {
  id: string;
  title: string;
  callNumber: string | null;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  status: string;
  allocation: number | null;
}

/**
 * Response for calls by project
 */
export interface ProjectCallsResponse {
  data: ProjectCall[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/**
 * Get calls for a specific project (admin endpoint).
 * Used in recommendation details page to show existing calls in project.
 * Lazy loaded only when user expands the project context section.
 *
 * @param projectId - UUID of the project to get calls for
 * @param limit - Maximum number of calls to return (default: PROJECT_CALLS constant, max: 50)
 * @returns Promise with paginated calls data including title, callNumber, status, dates, allocation
 * @throws Error if projectId is invalid UUID or API request fails
 * @example
 * const { calls, pagination } = await getCallsByProjectId('523e4567-e89b-12d3-a456-426614174000');
 * console.log(calls[0].title); // "Nabór wniosków..."
 * @see docs/specifications/2026-01-30-recommendation-details-project-context.md
 */
export const getCallsByProjectId = async (
  projectId: string,
  limit = API_PAGINATION_LIMITS.PROJECT_CALLS,
): Promise<ProjectCallsResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.append("projectId", projectId);
  searchParams.append("limit", limit.toString());

  const { data } = await apiClient.get(
    `/admin/grants/calls?${searchParams.toString()}`,
  );
  return data;
};

/**
 * Recommendation item for related recommendations section
 */
export interface ProjectRecommendation {
  id: string;
  suggestedAction: "create_new" | "update_existing" | "link_to_existing";
  status: "pending_review" | "approved" | "rejected" | "auto_approved";
  confidence: number;
  createdAt: string;
  article: {
    id: string;
    title: string;
  } | null;
  extractedDataSnapshot?: {
    applicationStartDate?: string | null;
    applicationEndDate?: string | null;
  };
}

/**
 * Response for recommendations by project
 */
export interface ProjectRecommendationsResponse {
  data: ProjectRecommendation[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/**
 * Get recommendations for a specific project (admin endpoint).
 * Used in recommendation details page to show related recommendations.
 * Lazy loaded only when user expands the project context section.
 *
 * @param projectId - UUID of the project to get recommendations for
 * @param limit - Maximum number of recommendations to return (default: PROJECT_RECOMMENDATIONS constant, max: 20)
 * @returns Promise with paginated recommendations data including article, action, status, confidence
 * @throws Error if projectId is invalid UUID or API request fails
 * @example
 * const { data, pagination } = await getRecommendationsByProjectId('523e4567-e89b-12d3-a456-426614174000');
 * console.log(data[0].suggestedAction); // "create_new"
 * @see docs/specifications/2026-01-30-recommendation-details-project-context.md
 */
export const getRecommendationsByProjectId = async (
  projectId: string,
  limit = API_PAGINATION_LIMITS.PROJECT_RECOMMENDATIONS,
): Promise<ProjectRecommendationsResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.append("projectId", projectId);
  searchParams.append("limit", limit.toString());
  searchParams.append("status", "all");

  const { data } = await apiClient.get(
    `/admin/grants/recommendations?${searchParams.toString()}`,
  );
  return data;
};

// --- Labels API ---

export interface GrantsLabelItem {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
}

export interface ArticleLabelMap {
  [articleId: string]: {
    labelId: string;
    labelName: string;
    color: string;
  };
}

export const getLabels = async (): Promise<GrantsLabelItem[]> => {
  const { data } = await apiClient.get("/grants/labels");
  return data;
};

export const createLabel = async (payload: {
  name: string;
  color: string;
}): Promise<GrantsLabelItem> => {
  const { data } = await apiClient.post("/grants/labels", payload);
  return data;
};

export const updateLabel = async (
  id: string,
  payload: { name?: string; color?: string; sortOrder?: number },
): Promise<GrantsLabelItem> => {
  const { data } = await apiClient.patch(`/grants/labels/${id}`, payload);
  return data;
};

export const deleteLabel = async (id: string): Promise<void> => {
  await apiClient.delete(`/grants/labels/${id}`);
};

export const setArticleLabel = async (
  articleType: "kfs" | "bur" | "call",
  articleId: string,
  labelId: string | null,
): Promise<{ labelId: string | null; color: string | null }> => {
  const { data } = await apiClient.put(
    `/grants/article-labels/${articleType}/${articleId}`,
    { labelId },
  );
  return data;
};

export const getArticleLabels = async (
  articleType: "kfs" | "bur" | "call",
  articleIds: string[],
): Promise<ArticleLabelMap> => {
  if (articleIds.length === 0) return {};
  const { data } = await apiClient.get(
    `/grants/article-labels/${articleType}?articleIds=${articleIds.join(",")}`,
  );
  return data;
};

/**
 * Get labour barometer data (deficit professions) for given districts.
 * Returns deficit and large deficit professions grouped by district,
 * plus optional voivodeship-level data.
 *
 * @param districtCodes - Array of TERYT 4-digit district codes
 * @param year - Optional year for barometer data (defaults to current year on server)
 * @returns Barometer data with district and voivodeship professions
 * @see docs/specifications/2026-03-06-kfs-barometr-zawodow.md
 */
export const getBarometerData = async (
  districtCodes: string[],
  year?: number,
): Promise<BarometerResponse> => {
  const searchParams = new URLSearchParams();
  districtCodes.forEach((code) => searchParams.append("districtCodes", code));
  if (year !== undefined) {
    searchParams.append("year", year.toString());
  }

  const { data } = await apiClient.get(
    `/grants/barometer?${searchParams.toString()}`,
  );
  return data;
};

// ============================================================
// Saved Views API (BC: grants/saved-views)
// @see docs/specifications/2026-03-04-saved-views.md
// ============================================================

/**
 * Preset scheme filter values for the CALLS context.
 * - 'kfs': NATIONAL_FUND programs only
 * - 'bur': all non-KFS programs (EU_REGIONAL + EU_NATIONAL + calls with NULL program)
 *
 * NOTE: 'pozostale' is NOT a valid calls scheme — calls without a program are
 * still "BUR" (complement semantics). For the articles context use
 * {@link ArticlesSchemeValue} instead. See spec
 * docs/specifications/2026-06-08-articles-filters-unification/spec.md
 * ("Kontrakt scheme (BC ownership)").
 */
export type SchemeValue = "kfs" | "bur";

/**
 * Preset scheme filter values for the ARTICLES context.
 * Adds 'pozostale' (residual bucket — articles whose source/call path resolves
 * to NO program). Articles use explicit per-type predicates, hence a distinct
 * three-valued type rather than extending {@link SchemeValue}.
 * - 'kfs': resolved program type = NATIONAL_FUND
 * - 'bur': resolved program type IN (EU_NATIONAL, EU_REGIONAL)
 * - 'pozostale': no resolved program (source without project and no call mapping)
 */
export type ArticlesSchemeValue = "kfs" | "bur" | "pozostale";

/**
 * Filters snapshot stored in a saved view.
 * Mirrors SavedViewFilters interface from backend DTO.
 */
export interface SavedViewFilters {
  programIds?: string[];
  competitionIds?: string[];
  statuses?: string[];
  regions?: string[];
  districts?: string[];
  operatorIds?: string[];
  projectIds?: string[];
  targetGroups?: string[];
  applicationEndDateFrom?: string;
  applicationEndDateTo?: string;
  allocationMin?: number;
  allocationMax?: number;
  labelIds?: string[];
  labelFilter?: string;
  search?: string;
  /** Labour barometer occupation names for filtering (max 3) */
  barometerOccupations?: string[];
  /** Labour barometer deficit statuses for filtering (max 4) */
  barometerStatuses?: string[];
  /** Preset scheme filter: 'kfs' = NATIONAL_FUND only; 'bur' = all non-KFS programs incl. NULL */
  scheme?: SchemeValue;
}

/** Discriminator for saved view context — 'calls' (default) or 'articles' */
export type SavedViewType = "calls" | "articles";

/**
 * Filters snapshot for article-centric saved views.
 * Separate from SavedViewFilters (calls) to avoid Zod .strip() silently dropping unknown fields.
 *
 * Field names mirror SavedViewFilters where the calls filter plugins are reused
 * 1:1 (program/project/operator/region/district/barometer/search), so the same
 * plugin serialization round-trips through the articles saved-view schema.
 * Adds `articleRoles` (article-specific) and an articles-flavoured `scheme`
 * (incl. 'pozostale').
 */
export interface ArticlesViewFilters {
  programIds?: string[];
  projectIds?: string[];
  operatorIds?: string[];
  regions?: string[];
  districts?: string[];
  /** Labour barometer occupation names for filtering (max 3) */
  barometerOccupations?: string[];
  /** Labour barometer deficit statuses for filtering (max 4) */
  barometerStatuses?: string[];
  articleRoles?: string[];
  search?: string;
  /** Articles scheme preset: 'kfs' | 'bur' | 'pozostale' */
  scheme?: ArticlesSchemeValue;
}

/**
 * Saved view item as returned by the API.
 * viewType is optional for backward compatibility with existing views (default: 'calls').
 */
export interface SavedViewItem {
  id: string;
  name: string;
  /** Top-level context discriminator (not inside filters JSONB). Defaults to 'calls'. */
  viewType?: SavedViewType;
  filters: SavedViewFilters | ArticlesViewFilters;
  createdAt: string;
  updatedAt: string;
}

/**
 * Get all saved views for the current user (default: viewType=calls).
 * Returns list ordered by created_at ASC.
 */
export const getSavedViews = async (): Promise<SavedViewItem[]> => {
  const { data } = await apiClient.get("/grants/saved-views?viewType=calls");
  return data;
};

/**
 * Get saved views for a specific viewType context.
 * Used by article-centric tab (viewType=articles).
 */
export const getSavedViewsByType = async (
  viewType: SavedViewType,
): Promise<SavedViewItem[]> => {
  const { data } = await apiClient.get(
    `/grants/saved-views?viewType=${encodeURIComponent(viewType)}`,
  );
  return data;
};

/**
 * Create a new saved view.
 * @throws 409 when limit of 10 views is reached
 */
export const createSavedView = async (payload: {
  name: string;
  filters: SavedViewFilters | ArticlesViewFilters;
  viewType?: SavedViewType;
}): Promise<SavedViewItem> => {
  const { data } = await apiClient.post("/grants/saved-views", payload);
  return data;
};

/**
 * Update an existing saved view (rename or update filters).
 * @throws 403 if user does not own the view
 */
export const updateSavedView = async (
  id: string,
  payload: {
    name?: string;
    // Union so articles-specific fields (articleRoles, scheme='pozostale') are
    // not lost when saving a dirty articles view. Backend persists by viewType.
    filters?: SavedViewFilters | ArticlesViewFilters;
  },
): Promise<SavedViewItem> => {
  const { data } = await apiClient.patch(`/grants/saved-views/${id}`, payload);
  return data;
};

/**
 * Delete a saved view.
 * @throws 403 if user does not own the view
 */
export const deleteSavedView = async (id: string): Promise<void> => {
  await apiClient.delete(`/grants/saved-views/${id}`);
};

// ============================================================
// Operator Projects with Calls API (BC: Competitions)
// @see docs/specifications/2026-03-16-operator-other-projects-in-recommendation.md
// ============================================================

/**
 * Article linked to a call via ArticleToCallMapping
 */
export interface OperatorProjectArticle {
  id: string;
  title: string;
  url: string;
}

/**
 * Call with articles for operator projects section
 */
export interface OperatorProjectCall {
  id: string;
  title: string;
  callNumber: string | null;
  status: string;
  articles: OperatorProjectArticle[];
}

/**
 * Project with calls for operator projects section
 */
export interface OperatorProject {
  id: string;
  name: string;
  /** Action code from parent competition (e.g. "FERS.01.03"), null if not set */
  actionCode: string | null;
  calls: OperatorProjectCall[];
}

/**
 * Response for operator projects with calls
 */
export interface OperatorProjectsWithCallsResponse {
  projects: OperatorProject[];
  totalProjectCount: number;
}

/**
 * Get projects with calls for a specific operator (admin endpoint).
 * Used in recommendation details page to show other projects of the same operator.
 * Lazy loaded only when user expands the section.
 *
 * @param operatorId - UUID of the operator
 * @param excludeProjectId - Optional UUID of project to exclude (current project)
 * @returns Promise with projects including nested calls and articles
 * @throws Error if operatorId is invalid UUID or API request fails
 * @see docs/specifications/2026-03-16-operator-other-projects-in-recommendation.md
 */
export const getProjectsWithCalls = async (
  operatorId: string,
  excludeProjectId?: string,
): Promise<OperatorProjectsWithCallsResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.append("operatorId", operatorId);
  if (excludeProjectId) {
    searchParams.append("excludeProjectId", excludeProjectId);
  }

  const { data } = await apiClient.get(
    `/admin/grants/projects/with-calls?${searchParams.toString()}`,
  );
  return data;
};

// ============================================================
// Shared Source Operators API (BC: Competitions)
// @see docs/specifications/2026-03-17-shared-source-operators-section.md
// ============================================================

/**
 * Operator that shares information sources with the given operator.
 * Includes nested projects with calls and articles.
 * @see docs/specifications/2026-06-05-shared-source-operators-expand-projects/spec.md
 */
export interface SharedSourceOperator {
  id: string;
  name: string;
  projects: OperatorProject[];
}

/**
 * Response for shared source operators query
 */
export interface SharedSourceOperatorsResponse {
  operators: SharedSourceOperator[];
  totalCount: number;
}

/**
 * Get operators that share information sources (same URL) with the given operator.
 * Used in recommendation details page to show related operators.
 * Eagerly loaded on mount (section expanded by default).
 *
 * @param operatorId - UUID of the operator to find shared source operators for
 * @returns Promise with operators list and total count
 * @throws Error if operatorId is invalid UUID or API request fails
 * @see docs/specifications/2026-03-17-shared-source-operators-section.md
 */
export const getSharedSourceOperators = async (
  operatorId: string,
): Promise<SharedSourceOperatorsResponse> => {
  const { data } = await apiClient.get(
    `/admin/grants/projects/shared-source-operators?operatorId=${encodeURIComponent(operatorId)}`,
  );
  return data;
};

// ============================================================
// Processing Errors API (BC: Monitoring)
// @see docs/specifications/2026-03-11-processing-pipeline-error-monitoring.md
// ============================================================

/**
 * Error type values for processing pipeline errors.
 */
export type ProcessingErrorType =
  | "PAYLOAD_TOO_LARGE"
  | "CONTENT_FETCH_FAILED"
  | "ZOD_VALIDATION_FAILED"
  | "CONTEXT_RESOLUTION_FAILED"
  | "ARTICLE_NOT_FOUND"
  | "LINK_NOT_FOUND"
  | "UNKNOWN";

/**
 * Processing error DTO as returned by the API.
 */
export interface ProcessingErrorDto {
  id: string;
  articleId: string | null;
  sourceId: string | null;
  linkId: string | null;
  errorType: ProcessingErrorType;
  errorMessage: string;
  occurrenceCount: number;
  firstOccurredAt: string;
  lastOccurredAt: string;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response for paginated processing errors list.
 */
export interface ProcessingErrorsResponse {
  data: ProcessingErrorDto[];
  total: number;
}

/**
 * Response for processing errors summary.
 */
export interface ProcessingErrorsSummaryResponse {
  unresolvedCount: number;
}

/**
 * Get paginated list of processing errors with optional filters.
 */
export const getProcessingErrors = async (params?: {
  sourceId?: string;
  errorType?: string;
  resolved?: string;
  limit?: number;
  offset?: number;
}): Promise<ProcessingErrorsResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.sourceId) searchParams.append("sourceId", params.sourceId);
  if (params?.errorType) searchParams.append("errorType", params.errorType);
  if (params?.resolved !== undefined && params.resolved !== "")
    searchParams.append("resolved", params.resolved);
  if (params?.limit) searchParams.append("limit", params.limit.toString());
  if (params?.offset !== undefined)
    searchParams.append("offset", params.offset.toString());

  const { data } = await apiClient.get(
    `/grants/admin/processing-errors?${searchParams.toString()}`,
  );
  return data;
};

/**
 * Get summary of unresolved processing errors count.
 */
export const getProcessingErrorsSummary =
  async (): Promise<ProcessingErrorsSummaryResponse> => {
    const { data } = await apiClient.get(
      "/grants/admin/processing-errors/summary",
    );
    return data;
  };

/**
 * Mark a processing error as resolved.
 */
export const resolveProcessingError = async (
  id: string,
): Promise<ProcessingErrorDto> => {
  const { data } = await apiClient.patch(
    `/grants/admin/processing-errors/${id}/resolve`,
  );
  return data;
};

// ============================================================
// Client-Facing Articles API (BC: Sourcing)
// @see docs/specifications/2026-06-06-grants-articles-tab/spec.md
// ============================================================

/** One call (nabor) linked to an article via ArticleToCallMapping */
export interface LinkedCall {
  mappingId: string;
  callId: string;
  callTitle: string;
  /** Article role for THIS specific article↔call relation (per pair, not per article) */
  articleRole: string;
  /** Badge "Ręcznie dodane" when MANUAL */
  mappingType: string;
  programId: string;
  programName: string;
  projectId: string | null;
  projectName: string | null;
}

/** One row in the article-centric list (one article with all its linked calls) */
export interface ArticleListItem {
  articleId: string;
  title: string;
  // No raw `url`: links go through the anti-scraping redirect proxy
  // (GET /grants/articles/:articleId/redirect), built client-side from articleId.
  /** Source host only (e.g. "rarr.rzeszow.pl") — for hover/source vetting; never the full URL */
  sourceDomain: string | null;
  /** ISO 8601 — first time added to DB */
  createdAt: string;
  /** ISO 8601 — last update */
  updatedAt: string;
  /** All calls linked to this article (shown as chips in the "Nabory" column) */
  calls: LinkedCall[];
}

/** Facet count for program/project filter dropdowns */
export interface ArticleFacetCount {
  id: string;
  name: string;
  count: number;
}

/** Facet count for articleRole filter */
export interface ArticleRoleCount {
  role: string;
  count: number;
}

/** Facet count for region/district dropdowns (slug-keyed) */
export interface ArticleSlugFacetCount {
  /** ASCII slug (region voivodeship slug or district TERYT slug) */
  slug: string;
  name: string;
  count: number;
}

/** Paginated response for GET /grants/articles (client-facing) */
export interface ArticlesPageResponse {
  data: ArticleListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  programCounts: ArticleFacetCount[];
  projectCounts: ArticleFacetCount[];
  /** All ArticleType enum values (zero-filled for count=0) */
  articleRoleCounts: ArticleRoleCount[];
  /** Faceted counts for the Operator dropdown (source→project / call→operator path) */
  operatorCounts?: ArticleFacetCount[];
  /** Faceted region voivodeship slugs available given the other filters */
  regionCounts?: ArticleSlugFacetCount[];
  /** Faceted district (powiat) counts available given the other filters */
  districtCounts?: ArticleSlugFacetCount[];
}

/** Query params for GET /grants/articles */
export interface FindArticlesParams {
  programIds?: string[];
  projectIds?: string[];
  operatorIds?: string[];
  /** Region voivodeship slugs (param name `region`, mirrors calls DTO) */
  regions?: string[];
  /** District (powiat) TERYT slugs */
  districts?: string[];
  barometerOccupations?: string[];
  barometerStatuses?: string[];
  articleRoles?: string[];
  /** Articles scheme preset (kfs|bur|pozostale) */
  scheme?: ArticlesSchemeValue;
  search?: string;
  sortBy?: "createdAt" | "updatedAt";
  sortOrder?: "ASC" | "DESC";
  page?: number;
  limit?: number;
}

/**
 * Fetch the client-facing, article-centric list (GET /grants/articles).
 * Requires grants:calls module access.
 */
export const getArticlesList = async (
  params?: FindArticlesParams,
): Promise<ArticlesPageResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.programIds?.length) {
    params.programIds.forEach((id) => searchParams.append("programIds", id));
  }
  if (params?.projectIds?.length) {
    params.projectIds.forEach((id) => searchParams.append("projectIds", id));
  }
  if (params?.operatorIds?.length) {
    params.operatorIds.forEach((id) => searchParams.append("operatorIds", id));
  }
  // Region param name is `region` (array) — mirrors FindAllCallsDto.
  if (params?.regions?.length) {
    params.regions.forEach((r) => searchParams.append("region", r));
  }
  if (params?.districts?.length) {
    params.districts.forEach((d) => searchParams.append("districts", d));
  }
  if (params?.barometerOccupations?.length) {
    params.barometerOccupations.forEach((o) =>
      searchParams.append("barometerOccupations", o),
    );
  }
  if (params?.barometerStatuses?.length) {
    params.barometerStatuses.forEach((s) =>
      searchParams.append("barometerStatuses", s),
    );
  }
  if (params?.articleRoles?.length) {
    params.articleRoles.forEach((r) => searchParams.append("articleRoles", r));
  }
  if (params?.scheme) searchParams.set("scheme", params.scheme);
  if (params?.search) searchParams.set("search", params.search);
  if (params?.sortBy) searchParams.set("sortBy", params.sortBy);
  if (params?.sortOrder) searchParams.set("sortOrder", params.sortOrder);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));

  const { data } = await apiClient.get(
    `/grants/articles?${searchParams.toString()}`,
  );
  return data;
};

export default apiClient;
